import {
  ElementRef,
  EventEmitter,
  Injectable,
  OnDestroy,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { RendererConstants } from '@hi-level/2d-renderer';
import {
  CutoutData,
  FloorRenderingService,
  TierData,
} from '@iqd/shared-drawing';
import { MD5 } from 'crypto-js';
import { clone, find, isEqual, round } from 'lodash-es';
import { BehaviorSubject, first, Subject, Subscription } from 'rxjs';
import { QuoteData } from './quote-data';
import { CloneQuoteData } from './quote-data-helper';
import { QuoteSessionService } from './quote-session.service';

@Injectable({
  providedIn: 'root',
})
export class DrawingSessionService implements OnDestroy {
  zoomLevel = new BehaviorSubject(1);
  minZoom = new BehaviorSubject(0.5);
  onComponentDrag = new Subject();
  quoteDataChange = new EventEmitter<any>();

  private dragSubscription: Subscription;
  private onCenterClickSubscription: Subscription;
  private quoteData: any;
  private drawing: any;

  constructor(
    private floorRenderingService: FloorRenderingService,
    private quoteSession: QuoteSessionService
  ) {}

  init(
    quoteData: any,
    drawing: any,
    options: any,
    rendererElement: ElementRef
  ): void {
    this.quoteData = quoteData;
    this.drawing = drawing;

    options.element = rendererElement;
    this.dragSubscription =
      this.floorRenderingService.onComponentDragStop.subscribe(
        this.onComponentDragStop.bind(this)
      );
    this.onCenterClickSubscription =
      this.floorRenderingService.onCenterClick.subscribe((currentZoom) => {
        this.zoomLevel.next(currentZoom);
      });
    this.floorRenderingService.init(options);
    this.renderFloor();
    this.zoomLevel.next(
      this.floorRenderingService.renderer.getCurrentZoomLevel().x
    );
    this.minZoom.next(this.floorRenderingService.renderer.minimumZoom);
  }

  onChanges(changes: SimpleChanges): void {
    if (changes.drawing && !changes.drawing.isFirstChange()) {
      this.drawing = changes.drawing.currentValue;
      this.renderFloor();
    }
    if (
      changes.floorData &&
      !changes.floorData.isFirstChange() &&
      this.gridChanged(changes.floorData)
    ) {
      this.quoteData = changes.floorData.currentValue;
      this.renderFloor();
    }
  }

  onZoomChange(newZoomLevel: number): void {
    this.zoomLevel.next(newZoomLevel);
    this.floorRenderingService.renderer.setZoomLevel(newZoomLevel);
  }

  private gridChanged(change: SimpleChange): boolean {
    if (change.previousValue && change.currentValue) {
      return (
        !isEqual(
          change.previousValue.FloorData.GridData,
          change.currentValue.FloorData.GridData
        ) ||
        !isEqual(
          change.previousValue.FloorData.TiersData,
          change.currentValue.FloorData.TiersData
        )
      );
    }
    return false;
  }

  addCutouts(cutouts: any[]): void {
    for (const cutout of cutouts) {
      this.floorRenderingService.renderer.addCutout(cutout.tierNumber, cutout);
    }
  }

  onComponentDragStop(component: any): void {
    if (component.name === 'Cutout') {
      const newX = round(component.x / RendererConstants.scalingFactor, 3);
      const newY = round(component.y / RendererConstants.scalingFactor, 3);
      const xDim = round(
        component.scaledXDim / RendererConstants.scalingFactor,
        3
      );
      const yDim = round(
        component.scaledYDim / RendererConstants.scalingFactor,
        3
      );
      const newHash = MD5(
        `Tier:${component.tier.number},X:${newX},Y:${newY},Z:0,Type:Cutout,XDim:${xDim},YDim:${yDim},ZDim:0`
      ).toString();
      this.onComponentDrag.next({
        hash: component.id,
        newHash,
        newX,
        newY,
        tier: component.tier.number - 1,
      });
      component.id = newHash;
    }
  }

  getImageUrl(): string {
    return this.floorRenderingService.getImageUrl();
  }

  setViewStyle(newStyle: string): void {
    this.floorRenderingService.setViewStyle(newStyle);
  }

  private renderFloor(): void {
    if (!this.quoteData) {
      return;
    }
    if (this.drawing && !this.quoteSession.isQuoteDataDirty) {
      this.floorRenderingService.render(this.drawing);
    } else {
      this.floorRenderingService.renderPolyline(
        this.quoteData.FloorData.GridData.XDim,
        this.quoteData.FloorData.GridData.YDim,
        this.quoteData.FloorData.TiersData.TierCount
      );
      this.quoteData.FloorData.TiersData.TierData.forEach(
        (tierData: TierData, tierNumber: number) => {
          tierData.CutoutData.forEach((cutoutData: CutoutData) => {
            this.floorRenderingService.renderer.addCutout(
              tierNumber,
              cutoutData
            );
          });
        }
      );
    }
  }

  addNewCutouts(formValue: any): any[] {
    const newCutouts = [];
    const qd = CloneQuoteData(this.quoteData);
    const tierData = qd.FloorData.TiersData.TierData;
    qd.TransformData = [];
    for (let i = 0; i < tierData.length; i++) {
      const tierNumber = i + 1;
      const addOnTier = formValue[`tier-${tierNumber}`];
      if (addOnTier) {
        const cutout = {
          X: 0,
          Y: 0,
          XDim: formValue.xDim,
          YDim: formValue.yDim,
          Hash: '',
        } as CutoutData;
        cutout.Hash = MD5(
          `Tier:${tierNumber},X:${cutout.X},Y:${cutout.Y},Z:0,Type:Cutout,XDim:${cutout.XDim},YDim:${cutout.YDim},ZDim:0`
        ).toString();
        qd.FloorData.TiersData.TierData[i].CutoutData.push(clone(cutout));
        const renderingCutout = cutout as any;
        renderingCutout.tierNumber = tierNumber - 1;
        newCutouts.push(renderingCutout);
      }
    }
    this.quoteSession.setQuoteData(qd);
    this.quoteDataChange.emit(qd);
    return newCutouts;
  }

  resizeCutouts(formValue: any, cutout: any): void {
    let cutoutData = null;
    let tierNumber = 0;
    this.quoteSession.quoteData
      .pipe(first())
      .subscribe((quoteData: QuoteData) => {
        const qd = CloneQuoteData(quoteData);
        for (let i = 0; i < qd.FloorData.TiersData.TierData.length; i++) {
          const tier = qd.FloorData.TiersData.TierData[i];
          cutoutData = find(tier.CutoutData, { Hash: cutout.id });
          if (cutoutData != null) {
            tierNumber = i + 1;
            break;
          }
        }
        if (cutoutData != null) {
          cutout.scaledXDim = formValue.xDim * RendererConstants.scalingFactor;
          cutout.scaledYDim = formValue.yDim * RendererConstants.scalingFactor;
          cutout.draw(
            cutout.position,
            cutout.scaledXDim,
            cutout.scaledYDim,
            1,
            0
          );

          cutoutData.XDim = formValue.xDim;
          cutoutData.YDim = formValue.yDim;
          const newHash = MD5(
            'Tier:' +
              tierNumber +
              ',X:' +
              cutoutData.X +
              ',Y:' +
              cutoutData.Y +
              ',Z:' +
              0 +
              ',Type:Cutout,XDim:' +
              cutoutData.XDim +
              ',YDim:' +
              cutoutData.YDim +
              ',ZDim:' +
              0
          ).toString();
          cutout.id = newHash;
          cutoutData.Hash = newHash;
          qd.TransformData = [];
        }
        this.quoteSession.setQuoteData(qd);
        return cutoutData;
      });
  }

  ngOnDestroy(): void {
    if (this.dragSubscription) {
      this.dragSubscription.unsubscribe();
    }
    if (this.onCenterClickSubscription) {
      this.onCenterClickSubscription.unsubscribe();
    }
  }
}
